<script setup>
// The resolved handicap for ONE match. Full mode is a two-column card — one column per side
// with the side's handicap (the pair's combined value when there are several players, listed
// underneath) and the start it plays from — all worded by
// the server (HandicapStart.home / .away). Compact mode is the one-line sentence (score entry).
// `start` is FixtureDetailResponse.handicapStart (null = level or handicaps off).
import {computed} from 'vue';
import {useHandicaps} from '~/composables/useHandicaps';

const props = defineProps({
  start: {type: Object, default: null},
  compact: {type: Boolean, default: false},
});
const {valueTone} = useHandicaps();
const visible = computed(() => props.start != null && !props.start.isLevel);
const unit = computed(() => props.start?.scoreboard?.unit ?? 'game');
const sides = computed(() => [props.start?.home, props.start?.away].filter(Boolean));
// The headline is what the side actually STARTS on once the two handicaps net off — that is the
// number players look for. The side's own handicap (a pair's combined one) is the small print.
const startOf = (i) => props.start?.scoreboard?.starts?.[i] ?? 0;
const headline = (side, i) => startOf(i) !== 0 ? side.boardDisplay : (side.startText === 'Scratch' ? 'Scratch' : 'Level');
// colour follows who the start favours (darts starts are both positive, so the sign is the guide)
const tone = (side, i) => startOf(i) === 0 ? 'level' : (side.sign > 0 ? 'receive' : side.sign < 0 ? 'owe' : 'level');
// one label for both sides — "Each game starts at" — then the value, big. No sentence repeating it.
const startLabel = computed(() => `Each ${unit.value} ${props.start?.played ? 'started' : 'starts'} at`);
// Tie-breaks get their own block under the two columns, both sides named on one line — the
// per-side rows were easy to miss. Level mode says so in words rather than showing two zeros.
// the cell reads like the headline above it: "−1", "2–0 up", "Level" — not a sentence
const tbValue = (side, tb) => {
  const text = side?.tiebreaks?.find((t) => t.unit === tb.unit)?.text ?? 'Level';
  return text.replace(/^Start(s|ed)( at)? /i, '');
};
const tbTone = (side, i, tb) => (tb.starts?.[i] ?? 0) === 0 ? 'level' : (side.sign > 0 ? 'receive' : side.sign < 0 ? 'owe' : 'level');
const tiebreaks = computed(() => (props.start?.tiebreaks ?? []).map((tb) => ({
  ...tb,
  homeText: tbValue(props.start.home, tb), awayText: tbValue(props.start.away, tb),
  homeTone: tbTone(props.start.home, 0, tb), awayTone: tbTone(props.start.away, 1, tb),
})));
const tiebreaksConverted = computed(() => props.start?.tiebreakMode === 1);
</script>

<template>
  <div v-if="visible && compact" class="hmb hmb--compact">
    <v-icon size="18" icon="mdi-scale-balance" color="primary"/>
    <div class="hmb-summary">{{ start.summary }}</div>
  </div>

  <div v-else-if="visible" class="hmb-card tl-card">
    <div class="hmb-head">
      <v-icon size="16" icon="mdi-scale-balance" color="primary"/>
      <span>Handicap</span>
    </div>
    <div class="hmb-cols">
      <div v-for="(side, i) in sides" :key="i" class="hmb-side" :class="{'hmb-side--level': side.sign === 0}">
        <div class="hmb-name">{{ side.name }}</div>
        <div class="hmb-startlabel">{{ startLabel }}</div>
        <div class="hmb-board" :class="`hmb-board--${tone(side, i)}`">{{ headline(side, i) }}</div>
        <div v-if="side.patternNote" class="hmb-pattern">{{ side.patternNote }}</div>
        <div v-if="!side.players?.length && side.label" class="hmb-hcp">
          <span class="hmb-hcp__label">Handicap</span>
          <span class="tl-chip hmb-chip" :class="valueTone(side.sign)">{{ side.label }}</span>
        </div>
        <div v-if="side.players?.length" class="hmb-players">
          <div v-for="p in side.players" :key="p.name" class="hmb-player">
            <span class="hmb-player-name">{{ p.name }}</span>
            <span class="tl-chip hmb-chip" :class="valueTone(p.sign)">{{ p.label }}</span>
          </div>
          <div class="hmb-player hmb-player--total">
            <span class="hmb-player-name">Combined</span>
            <span class="tl-chip hmb-chip" :class="valueTone(side.sign)">{{ side.label }}</span>
          </div>
        </div>
      </div>
    </div>
    <div v-if="tiebreaks.length" class="hmb-tbs">
      <div class="hmb-tbs__head">
        <v-icon size="14" icon="mdi-timer-sand" color="primary"/>
        Tie-breaks · {{ tiebreaksConverted ? 'handicap converted to a points start' : 'played level (off scratch)' }}
      </div>
      <table class="hmb-tbs__table">
        <thead>
          <tr>
            <th>Each tie-break starts at</th>
            <th>{{ start.home.name }}</th>
            <th>{{ start.away.name }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="tb in tiebreaks" :key="tb.unit">
            <td class="hmb-tbs__label">{{ tb.label }}</td>
            <td class="hmb-tbs__value" :class="`hmb-board--${tb.homeTone}`">{{ tb.homeText }}</td>
            <td class="hmb-tbs__value" :class="`hmb-board--${tb.awayTone}`">{{ tb.awayText }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-if="start.valuesHidden" class="hmb-note">Handicap values are hidden by the club while they are being set. The start above is what the match is played off.</div>
    <div v-if="start.entryHint" class="hmb-note">{{ start.entryHint.note }}</div>
  </div>
</template>

<style scoped lang="scss">
.hmb {
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 8px 12px;
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-left: 3px solid rgb(var(--v-theme-primary));
  border-radius: 10px;
  background: rgb(var(--v-theme-light));
  font-size: 13px;
}
.hmb-summary { font-weight: 600; line-height: 1.4; }

.hmb-card { padding: 12px 14px 10px; }
.hmb-head {
  display: flex; align-items: center; gap: 6px;
  font-size: 10px; font-weight: 700; letter-spacing: .6px; text-transform: uppercase;
  color: rgb(var(--v-theme-textSubtle)); margin-bottom: 10px;
}
.hmb-cols { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.hmb-side {
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 10px; padding: 10px 12px;
  background: rgb(var(--v-theme-light));
  display: flex; flex-direction: column; gap: 4px;
  &--level { opacity: .75; }
}
.hmb-name { font-weight: 700; font-size: 13px; line-height: 1.3; }
.hmb-board { font-size: 26px; font-weight: 800; line-height: 1.1; color: rgb(var(--v-theme-primary)); }
.hmb-board--receive { color: rgb(var(--v-theme-success)); }
.hmb-board--owe { color: rgb(var(--v-theme-warning)); }
.hmb-board--level { color: rgb(var(--v-theme-textSubtle)); }
.hmb-player--total { font-weight: 700; }
.hmb-startlabel { font-size: 10.5px; font-weight: 700; letter-spacing: .5px; text-transform: uppercase; color: rgb(var(--v-theme-textSubtle)); margin-top: 6px; }
.hmb-pattern { font-size: 11.5px; color: rgb(var(--v-theme-textSubtle)); }
.hmb-players { margin-top: 6px; border-top: 1px dashed rgb(var(--v-theme-borderLight)); padding-top: 6px; display: flex; flex-direction: column; gap: 3px; }
.hmb-player { display: flex; justify-content: space-between; align-items: center; gap: 8px; font-size: 12px; }
.hmb-player-name { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.hmb-chip { font-size: 10px; font-weight: 700; padding: 0 6px; border-radius: 999px; white-space: nowrap; }
.hmb-note { font-size: 12px; color: rgb(var(--v-theme-textSubtle)); margin-top: 8px; }
@media (max-width: 520px) { .hmb-cols { grid-template-columns: 1fr; } }

.hmb-hcp { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-top: 8px; padding-top: 6px; border-top: 1px dashed rgb(var(--v-theme-borderLight)); font-size: 12px; }
.hmb-hcp__label { color: rgb(var(--v-theme-textSubtle)); font-weight: 600; }
.hmb-tbs {
  margin-top: 10px; padding: 8px 12px; border-radius: 10px;
  background: rgb(var(--v-theme-light)); border: 1px solid rgb(var(--v-theme-borderLight));
  font-size: 12.5px;
}
.hmb-tbs__head {
  display: flex; align-items: center; gap: 6px;
  font-size: 10px; font-weight: 700; letter-spacing: .6px; text-transform: uppercase;
  color: rgb(var(--v-theme-textSubtle)); margin-bottom: 6px;
}
.hmb-tbs__table { width: 100%; table-layout: fixed; border-collapse: collapse; font-size: 12.5px; }
.hmb-tbs__table th { text-align: left; font-size: 10.5px; font-weight: 700; letter-spacing: .4px; text-transform: uppercase; color: rgb(var(--v-theme-textSubtle)); padding: 4px 8px 6px 0; border-bottom: 1px solid rgb(var(--v-theme-borderLight)); vertical-align: bottom; }
.hmb-tbs__table th:first-child { width: 34%; }
.hmb-tbs__table td { padding: 7px 8px 7px 0; vertical-align: middle; border-bottom: 1px solid rgb(var(--v-theme-borderLight)); }
.hmb-tbs__table tr:last-child td { border-bottom: 0; }
.hmb-tbs__label { font-weight: 700; color: rgb(var(--v-theme-text)); }
.hmb-tbs__value { font-size: 17px; font-weight: 800; line-height: 1.1; }
</style>
